import { useEffect, useMemo, useState } from "react";
import { Milk, Plus, Trash2, Pencil, Baby } from "lucide-react";
import { api, money, num, trDate, todayISO } from "../lib/api";
import { PageHeader, Modal, FormField, SubmitButton, useForm, EmptyState, StatMini } from "../components/farm/FarmUI";
import { HERO, HERO_TINT } from "../lib/heroes";

const STATUS_LABEL = { alive: "Yaşıyor", sold: "Satıldı", died: "Öldü" };

function ageInMonths(birth) {
  if (!birth) return null;
  try {
    const d = new Date(birth);
    const now = new Date();
    const months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
    return Math.max(0, months);
  } catch {
    return null;
  }
}

function ageText(birth) {
  const m = ageInMonths(birth);
  if (m === null) return "-";
  if (m < 12) return `${m} aylık`;
  const y = Math.floor(m / 12);
  const rem = m % 12;
  return rem ? `${y} yaş ${rem} ay` : `${y} yaşında`;
}

const emptyForm = {
  tag_no: "", name: "", gender: "female", birth_date: "",
  mother_tag: "", status: "alive", weight_kg: "", note: "",
};

export default function Cattle() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const { form, set, setForm, reset } = useForm(emptyForm);

  const load = () => api.get("/cattle").then(setItems).catch(() => setError("Yüklenemedi."));
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const alive = items.filter((c) => c.status === "alive");
    const calves = alive.filter((c) => {
      const m = ageInMonths(c.birth_date);
      return m !== null && m < 12;
    });
    const females = alive.filter((c) => c.gender === "female").length;
    const males = alive.filter((c) => c.gender === "male").length;
    const soldTotal = items.filter((c) => c.status === "sold").reduce((a, c) => a + Number(c.sold_amount || 0), 0);
    return { alive: alive.length, calves: calves.length, females, males, soldTotal };
  }, [items]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    if (filter === "calf") return items.filter((c) => c.status === "alive" && ageInMonths(c.birth_date) !== null && ageInMonths(c.birth_date) < 12);
    return items.filter((c) => c.status === filter);
  }, [items, filter]);

  const submit = async (e) => {
    e.preventDefault();
    const base = {
      name: form.name,
      status: form.status,
      weight_kg: Number(form.weight_kg) || 0,
      note: form.note,
    };
    try {
      if (editing) {
        const patch = { ...base };
        if (form.status === "sold") { patch.sold_amount = Number(form.sold_amount) || 0; patch.sold_date = form.sold_date || null; }
        if (form.status === "died") patch.died_date = form.died_date || null;
        const updated = await api.patch(`/cattle/${editing.id}`, patch);
        setItems((x) => x.map((i) => (i.id === editing.id ? updated : i)));
      } else {
        const payload = {
          tag_no: form.tag_no,
          gender: form.gender,
          birth_date: form.birth_date || null,
          mother_tag: form.mother_tag,
          ...base,
        };
        const rec = await api.post("/cattle", payload);
        setItems((x) => [rec, ...x]);
      }
      reset();
      setEditing(null);
      setOpen(false);
    } catch { setError("Kaydedilemedi."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu hayvan kaydı silinecek. Emin misiniz?")) return;
    try { await api.del(`/cattle/${id}`); setItems((x) => x.filter((i) => i.id !== id)); }
    catch { setError("Silinemedi."); }
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      tag_no: c.tag_no, name: c.name || "", gender: c.gender,
      birth_date: c.birth_date || "", mother_tag: c.mother_tag || "",
      status: c.status, weight_kg: c.weight_kg || "",
      sold_amount: c.sold_amount || "", sold_date: c.sold_date || "",
      died_date: c.died_date || "", note: c.note || "",
    });
    setOpen(true);
  };

  const openNew = () => { reset(); setEditing(null); setOpen(true); };

  return (
    <>
      <PageHeader
        eyebrow="BÜYÜKBAŞ · SÜT SÜRÜSÜ"
        title="Büyükbaş hayvanlar"
        subtitle="İnek, dana ve buzağıların kart bazlı takibi."
        heroImage={HERO.cattle}
        heroTint={HERO_TINT.cattle}
        actions={
          <button className="primary-btn" data-testid="cattle-add-btn" onClick={openNew}>
            <Plus size={17} /> Hayvan ekle
          </button>
        }
      />

      {error && <div className="api-error" data-testid="cattle-error">{error}</div>}

      <section className="stats-mini-row">
        <StatMini label="Mevcut" value={`${stats.alive}`} tone="teal" testId="cattle-stat-alive" />
        <StatMini label="Buzağı (0-12 ay)" value={`${stats.calves}`} tone="orange" testId="cattle-stat-calves" />
        <StatMini label="Dişi" value={`${stats.females}`} tone="green" testId="cattle-stat-females" />
        <StatMini label="Erkek" value={`${stats.males}`} tone="teal" testId="cattle-stat-males" />
        <StatMini label="Satış geliri" value={money(stats.soldTotal)} tone="green" testId="cattle-stat-sold" />
      </section>

      <div className="filter-row">
        <button className={`chip ${filter === "all" ? "active" : ""}`} data-testid="cattle-filter-all" onClick={() => setFilter("all")}>Tümü</button>
        <button className={`chip ${filter === "alive" ? "active" : ""}`} data-testid="cattle-filter-alive" onClick={() => setFilter("alive")}>Yaşayan</button>
        <button className={`chip ${filter === "calf" ? "active" : ""}`} data-testid="cattle-filter-calf" onClick={() => setFilter("calf")}>Buzağılar</button>
        <button className={`chip ${filter === "sold" ? "active" : ""}`} data-testid="cattle-filter-sold" onClick={() => setFilter("sold")}>Satılan</button>
        <button className={`chip ${filter === "died" ? "active" : ""}`} data-testid="cattle-filter-died" onClick={() => setFilter("died")}>Ölen</button>
      </div>

      <div className="panel">
        {filtered.length === 0 ? (
          <EmptyState icon={Milk} title={items.length === 0 ? "Henüz büyükbaş yok" : "Bu filtrede kayıt yok"} text="Küpe numarası, cinsiyet ve doğum tarihi ile her hayvanı kart olarak takip edin. Buzağılar otomatik ayrı listelenir." actionLabel={items.length === 0 ? "İlk hayvanı ekle" : "Hayvan ekle"} onAction={openNew} testId="cattle-empty-add" />
        ) : (
          <div className="cattle-grid">
            {filtered.map((c) => {
              const isCalf = c.status === "alive" && ageInMonths(c.birth_date) !== null && ageInMonths(c.birth_date) < 12;
              return (
                <div className="cattle-card" key={c.id} data-testid={`cattle-item-${c.id}`}>
                  <div className="cattle-head">
                    <div className={`cattle-icon ${c.gender}`}>
                      {isCalf ? <Baby size={20} /> : <Milk size={20} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <b>#{c.tag_no}{c.name ? ` · ${c.name}` : ""}</b>
                      <small>{ageText(c.birth_date)} · Doğum {trDate(c.birth_date)}</small>
                    </div>
                  </div>

                  <div className="cattle-badges">
                    <span className={`cattle-badge ${c.status}`}>{STATUS_LABEL[c.status]}</span>
                    <span className={`cattle-badge ${c.gender}`}>{c.gender === "female" ? "Dişi" : "Erkek"}</span>
                    {isCalf && <span className="cattle-badge calf">Buzağı</span>}
                  </div>

                  <div className="cattle-meta">
                    <div><span>Ağırlık</span><b>{c.weight_kg ? `${num(c.weight_kg)} kg` : "-"}</b></div>
                    <div><span>Ana küpe</span><b>{c.mother_tag || "-"}</b></div>
                    {c.status === "sold" && <div><span>Satış tutarı</span><b className="positive">{money(c.sold_amount)}</b></div>}
                    {c.status === "sold" && <div><span>Satış tarihi</span><b>{trDate(c.sold_date)}</b></div>}
                    {c.status === "died" && <div><span>Ölüm tarihi</span><b>{trDate(c.died_date)}</b></div>}
                  </div>

                  {c.note && <p className="cattle-note">{c.note}</p>}

                  <div className="cattle-actions">
                    <button className="ghost-btn small" data-testid={`cattle-edit-${c.id}`} onClick={() => openEdit(c)}>
                      <Pencil size={13} /> Durum güncelle
                    </button>
                    <button className="delete-btn always" data-testid={`cattle-delete-${c.id}`} onClick={() => remove(c.id)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {open && (
        <Modal
          title={editing ? `Kartı güncelle · #${editing.tag_no}` : "Yeni büyükbaş"}
          subtitle={editing ? "GÜNCELLE" : "BÜYÜKBAŞ"}
          onClose={() => { setOpen(false); setEditing(null); }}
        >
          <form onSubmit={submit}>
            {!editing && (
              <>
                <div className="form-row">
                  <FormField label="Küpe no">
                    <input required data-testid="cattle-form-tag" placeholder="TR1234" value={form.tag_no} onChange={(e) => set("tag_no", e.target.value)} />
                  </FormField>
                  <FormField label="Ad (opsiyonel)">
                    <input data-testid="cattle-form-name" placeholder="Örn. Sarıkız" value={form.name} onChange={(e) => set("name", e.target.value)} />
                  </FormField>
                </div>
                <div className="form-row">
                  <FormField label="Cinsiyet">
                    <select required data-testid="cattle-form-gender" value={form.gender} onChange={(e) => set("gender", e.target.value)}>
                      <option value="female">Dişi</option>
                      <option value="male">Erkek</option>
                    </select>
                  </FormField>
                  <FormField label="Doğum tarihi">
                    <input type="date" data-testid="cattle-form-birth" value={form.birth_date} onChange={(e) => set("birth_date", e.target.value)} />
                  </FormField>
                </div>
                <FormField label="Ana küpe (buzağıysa)">
                  <input data-testid="cattle-form-mother" placeholder="Örn. TR9876" value={form.mother_tag} onChange={(e) => set("mother_tag", e.target.value)} />
                </FormField>
              </>
            )}
            {editing && (
              <FormField label="Ad">
                <input data-testid="cattle-form-name-edit" value={form.name} onChange={(e) => set("name", e.target.value)} />
              </FormField>
            )}
            <div className="form-row">
              <FormField label="Durum">
                <select required data-testid="cattle-form-status" value={form.status} onChange={(e) => set("status", e.target.value)}>
                  <option value="alive">Yaşıyor</option>
                  <option value="sold">Satıldı</option>
                  <option value="died">Öldü</option>
                </select>
              </FormField>
              <FormField label="Ağırlık (kg)">
                <input type="number" step="1" data-testid="cattle-form-weight" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} />
              </FormField>
            </div>
            {form.status === "sold" && (
              <div className="form-row">
                <FormField label="Satış tutarı (₺)">
                  <input type="number" step="1" data-testid="cattle-form-sold-amount" value={form.sold_amount || ""} onChange={(e) => set("sold_amount", e.target.value)} />
                </FormField>
                <FormField label="Satış tarihi">
                  <input type="date" data-testid="cattle-form-sold-date" value={form.sold_date || ""} onChange={(e) => set("sold_date", e.target.value)} />
                </FormField>
              </div>
            )}
            {form.status === "died" && (
              <FormField label="Ölüm tarihi">
                <input type="date" data-testid="cattle-form-died-date" value={form.died_date || ""} onChange={(e) => set("died_date", e.target.value)} />
              </FormField>
            )}
            <FormField label="Not">
              <textarea data-testid="cattle-form-note" value={form.note} onChange={(e) => set("note", e.target.value)} />
            </FormField>
            <SubmitButton testId="cattle-form-submit">{editing ? "Güncelle" : "Kaydet"}</SubmitButton>
          </form>
        </Modal>
      )}
    </>
  );
}
